import { useState, useCallback, useEffect, useRef } from 'react';
import UnifiedAuthService, {
  AuthMethod,
  AuthMethodInfo,
  UnifiedAuthRequestParams,
  UnifiedAuthResult,
} from '../libs/UnifiedAuthService';
import { EZIOKProvider } from '../libs/EZIOKService';
import axiosClient from '../libs/axiosClient';
import {
  logAuthCallback,
  logAuthSuccess,
  logAuthFailure,
  logAuthAPIError,
  logCallbackParseError,
  logAuthError,
  addAuthBreadcrumb,
} from '../libs/sentryAuth';

// 글로벌 콜백 함수를 위한 Window 확장 (이미 다른 훅에서 선언됨)
declare global {
  interface Window {
    mobileOKCallback?: (response: string) => void;
    eziokCallback?: (response: string) => void;
  }
}

interface UseUnifiedAuthOptions {
  // 사용자 ID (로그인 상태일 경우, DB 저장용)
  userId?: number;
  // 인증 성공 콜백
  onSuccess?: (result: UnifiedAuthResult) => void;
  // 인증 실패 콜백
  onError?: (error: Error) => void;
  // 인증 취소 콜백
  onCancel?: () => void;
}

interface UseUnifiedAuthReturn {
  // 통합 인증 시작
  startAuth: (params: UnifiedAuthRequestParams) => Promise<void>;
  // 인증 방식 목록 조회
  getAuthMethods: () => Promise<AuthMethodInfo | null>;
  // 간편인증 사업자 목록 조회
  getEZIOKProviders: () => Promise<EZIOKProvider[]>;
  // 로딩 상태
  isLoading: boolean;
  // 에러 상태
  error: Error | null;
  // SDK 로드 상태
  sdkStatus: { mobileOK: boolean; eziok: boolean };
  // 인증 방식 목록
  authMethods: AuthMethodInfo[];
  // 간편인증 사업자 목록
  eziokProviders: EZIOKProvider[];
  // 현재 선택된 인증 방식
  currentAuthMethod: AuthMethod | null;
  // 인증 방식 변경
  setCurrentAuthMethod: (method: AuthMethod) => void;
}

/**
 * 통합 본인인증 커스텀 훅
 * Mobile-OK(휴대폰 인증)과 EZ-IOK(간편인증)을 통합 관리
 *
 * @example
 * const {
 *   startAuth,
 *   getAuthMethods,
 *   getEZIOKProviders,
 *   isLoading,
 *   authMethods,
 *   eziokProviders,
 * } = useUnifiedAuth({
 *   onSuccess: (result) => console.log('인증 성공:', result),
 *   onError: (error) => console.error('인증 실패:', error),
 * });
 *
 * // 휴대폰 인증 시작
 * await startAuth({
 *   authMethod: 'mobile',
 *   resultUrl: `https://api.dietdr.co.kr/func/mobileok/callback`,
 *   usageCode: '01005',
 * });
 *
 * // 간편인증 시작 (통합창)
 * await startAuth({
 *   authMethod: 'easy',
 *   resultUrl: `https://api.dietdr.co.kr/func/eziok/callback`,
 *   authProviderCode: 'kakao',
 * });
 */
export function useUnifiedAuth(options?: UseUnifiedAuthOptions): UseUnifiedAuthReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [sdkStatus, setSdkStatus] = useState({ mobileOK: false, eziok: false });
  const [authMethods, setAuthMethods] = useState<AuthMethodInfo[]>([]);
  const [eziokProviders, setEziokProviders] = useState<EZIOKProvider[]>([]);
  const [currentAuthMethod, setCurrentAuthMethod] = useState<AuthMethod | null>(null);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // redirect 인증 결과 체크 (마운트 시)
  // diethelper-front와 동일한 방식: sessionStorage의 mobileok_authResult, eziok_authResult 키 사용
  useEffect(() => {
    // Mobile-OK redirect 결과 체크
    // MobileOKCallback에서 저장하는 형식: MobileOKResult (resultCode, ci, di, name 등 직접 접근)
    const mobileOKResultStr = sessionStorage.getItem('mobileok_authResult');
    if (mobileOKResultStr) {
      console.log('[useUnifiedAuth] Found MobileOK redirect result');
      addAuthBreadcrumb('MobileOK redirect result found', 'redirect', {}, 'info');
      try {
        const result = JSON.parse(mobileOKResultStr);
        sessionStorage.removeItem('mobileok_authResult');

        // MobileOKResult 형식: { resultCode, resultMsg, ci, di, name, ... }
        const successCodes = ['0', '0000', '2000'];
        if (successCodes.includes(String(result.resultCode))) {
          const unifiedResult: UnifiedAuthResult = {
            authMethod: 'mobile',
            resultCode: result.resultCode,
            resultMsg: result.resultMsg || '인증 성공',
            ci: result.ci,
            di: result.di,
            name: result.name,
            birthDate: result.birthDate,
            gender: result.gender,
            nationalInfo: result.nationalInfo,
            mobileNo: result.mobileNo,
            mobileCo: result.mobileCo,
          };
          console.log('[useUnifiedAuth] MobileOK redirect success:', unifiedResult);
          logAuthSuccess('mobile', { redirect: true });
          optionsRef.current?.onSuccess?.(unifiedResult);
        } else {
          const error = new Error(result.resultMsg || '본인확인에 실패했습니다.');
          console.error('[useUnifiedAuth] MobileOK redirect error:', error);
          logAuthFailure('mobile', result.resultMsg || '본인확인 실패', { redirect: true });
          optionsRef.current?.onError?.(error);
        }
      } catch (err) {
        console.error('[useUnifiedAuth] Failed to parse MobileOK redirect result:', err);
        logCallbackParseError('mobile', mobileOKResultStr, err instanceof Error ? err : new Error('파싱 실패'));
      }
    }

    // EZ-IOK redirect 결과 체크
    const eziokResultStr = sessionStorage.getItem('eziok_authResult');
    if (eziokResultStr) {
      console.log('[useUnifiedAuth] Found EZIOK redirect result');
      addAuthBreadcrumb('EZIOK redirect result found', 'redirect', {}, 'info');
      try {
        const result = JSON.parse(eziokResultStr);
        sessionStorage.removeItem('eziok_authResult');

        if (result.success && result.data) {
          const unifiedResult: UnifiedAuthResult = {
            authMethod: 'easy',
            resultCode: '0000',
            resultMsg: result.message || '인증 성공',
            ci: result.data.ci,
            di: result.data.di,
            name: result.data.name,
            birthDate: result.data.birthDate,
            gender: result.data.gender,
            nationalInfo: result.data.nationalInfo,
            mobileNo: result.data.mobileNo,
            mobileCo: result.data.mobileCo,
          };
          console.log('[useUnifiedAuth] EZIOK redirect success:', unifiedResult);
          logAuthSuccess('easy', { redirect: true });
          optionsRef.current?.onSuccess?.(unifiedResult);
        } else {
          const error = new Error(result.message || '간편인증에 실패했습니다.');
          console.error('[useUnifiedAuth] EZIOK redirect error:', error);
          logAuthFailure('easy', result.message || '간편인증 실패', { redirect: true });
          optionsRef.current?.onError?.(error);
        }
      } catch (err) {
        console.error('[useUnifiedAuth] Failed to parse EZIOK redirect result:', err);
        logCallbackParseError('easy', eziokResultStr, err instanceof Error ? err : new Error('파싱 실패'));
      }
    }
  }, []);

  // SDK 사전 로드 및 글로벌 콜백 함수 등록
  useEffect(() => {
    // SDK 로드
    UnifiedAuthService.loadSDKs()
      .then((status) => setSdkStatus(status))
      .catch((err) => {
        console.error('[useUnifiedAuth] SDK load failed:', err);
        logAuthError(err instanceof Error ? err : new Error(String(err)), {}, { 'auth.stage': 'sdk-load' });
      });

    // 디버그: 콜백 함수 등록 확인
    console.log('[UnifiedAuth] Registering global callbacks...');
    console.log('[UnifiedAuth] Before - mobileOKCallback:', typeof window.mobileOKCallback);
    console.log('[UnifiedAuth] Before - eziokCallback:', typeof window.eziokCallback);

    // Mobile-OK 콜백 등록
    window.mobileOKCallback = async (response: string) => {
      console.log('[UnifiedAuth] MobileOK callback received:', response);
      // Sentry: 콜백 수신 로깅
      logAuthCallback('mobile', response);

      try {
        const data = JSON.parse(response);
        console.log('[UnifiedAuth] Parsed MobileOK data:', data);

        // diethelper-front 호환: 성공 코드 배열 체크
        const successCodes = ['0', '0000', '2000'];
        if (successCodes.includes(String(data.resultCode))) {
          // mokToken이 있으면 백엔드 /result API 호출하여 DB에 저장
          const mokToken = data.encryptMOKKeyToken || data.mokToken;
          const storedTxId = sessionStorage.getItem('mobileok_txId');

          if (mokToken && storedTxId) {
            console.log('[UnifiedAuth] Calling /result API with mokToken and txId:', storedTxId);

            try {
              const resultResponse = await axiosClient.post('/func/mobileok/result', {
                mokToken,
                txId: storedTxId,
                userId: optionsRef.current?.userId,
              });

              console.log('[UnifiedAuth] MobileOK result response:', resultResponse.data);

              // 세션 정리
              sessionStorage.removeItem('mobileok_txId');

              const resultData = resultResponse.data;

              if (resultData.resultCode === '0000') {
                const result: UnifiedAuthResult = {
                  authMethod: 'mobile',
                  resultCode: resultData.resultCode,
                  resultMsg: resultData.resultMsg || '인증 성공',
                  ci: resultData.ci,
                  di: resultData.di,
                  name: resultData.name,
                  birthDate: resultData.birthDate,
                  gender: resultData.gender,
                  nationalInfo: resultData.nationalInfo,
                  mobileNo: resultData.mobileNo,
                  mobileCo: resultData.mobileCo,
                };
                // Sentry: 인증 성공 로깅
                logAuthSuccess('mobile', { txId: storedTxId, resultCode: resultData.resultCode });
                optionsRef.current?.onSuccess?.(result);
              } else {
                const error = new Error(resultData.resultMsg || '본인확인 결과 조회에 실패했습니다.');
                // Sentry: 인증 실패 로깅
                logAuthFailure('mobile', resultData.resultMsg || '본인확인 결과 조회 실패', {
                  txId: storedTxId,
                  resultCode: resultData.resultCode,
                  resultMsg: resultData.resultMsg,
                  rawResponse: response,
                });
                optionsRef.current?.onError?.(error);
              }
            } catch (apiErr) {
              console.error('[UnifiedAuth] MobileOK result API error:', apiErr);
              // Sentry: API 호출 에러 로깅 (warning - 폴백 처리됨)
              logAuthAPIError('mobile', '/func/mobileok/result', apiErr, {
                txId: storedTxId,
                rawResponse: response,
              });
              // API 호출 실패해도 기존 data로 성공 처리 (DB 저장만 실패)
              const result: UnifiedAuthResult = {
                authMethod: 'mobile',
                resultCode: data.resultCode,
                resultMsg: data.resultMsg || '인증 성공',
                ...data,
              };
              optionsRef.current?.onSuccess?.(result);
            }
          } else {
            // mokToken이 없으면 기존 방식 (SDK 결과 직접 사용)
            const result: UnifiedAuthResult = {
              authMethod: 'mobile',
              resultCode: data.resultCode,
              resultMsg: data.resultMsg || '인증 성공',
              ...data,
            };
            optionsRef.current?.onSuccess?.(result);
          }
        } else {
          const error = new Error(data.resultMsg || '본인확인에 실패했습니다.');
          // Sentry: 인증 실패 로깅 (SDK 응답에서 실패)
          logAuthFailure('mobile', data.resultMsg || '본인확인 실패', {
            resultCode: data.resultCode,
            resultMsg: data.resultMsg,
            rawResponse: response,
          });
          optionsRef.current?.onError?.(error);
        }
      } catch (err) {
        console.error('[UnifiedAuth] MobileOK callback parse error:', err);
        // Sentry: 콜백 파싱 에러 로깅
        logCallbackParseError('mobile', response, err instanceof Error ? err : new Error('콜백 처리 실패'));
        optionsRef.current?.onError?.(err instanceof Error ? err : new Error('콜백 처리 실패'));
      }
    };

    console.log('[UnifiedAuth] After - mobileOKCallback:', typeof window.mobileOKCallback);

    // EZ-IOK 콜백 등록
    // 콜백 형식: JSON 또는 resultCode|keyToken|errorMsg (파이프 구분)
    window.eziokCallback = async (response: string) => {
      console.log('[UnifiedAuth] EZIOK callback received:', response);
      // Sentry: 콜백 수신 로깅
      logAuthCallback('easy', response);

      try {
        // diethelper-front 호환: JSON 형식 지원
        if (response.startsWith('{')) {
          const data = JSON.parse(response);
          console.log('[UnifiedAuth] Parsed EZIOK JSON data:', data);

          const successCodes = ['0', '0000', '2000'];
          if (successCodes.includes(String(data.resultCode))) {
            const storedTxId = sessionStorage.getItem('eziok_txId');
            if (!storedTxId) {
              throw new Error('유효하지 않은 인증 요청입니다. (txId 없음)');
            }

            // keyToken 추출 (다양한 필드명 지원)
            const keyToken = data.keyToken || data.encryptMOKKeyToken || data.token || '';

            if (keyToken) {
              console.log('[UnifiedAuth] Calling /result API with keyToken and txId:', storedTxId);

              // diethelper-front와 동일하게 상대 경로 사용
              const resultResponse = await axiosClient.post('/func/eziok/result', {
                keyToken,
                txId: storedTxId,
                userId: optionsRef.current?.userId,
              });

              console.log('[UnifiedAuth] EZIOK result response:', resultResponse.data);
              sessionStorage.removeItem('eziok_txId');

              const resultData = resultResponse.data;
              if (resultData.resultCode === '0000') {
                const result: UnifiedAuthResult = {
                  authMethod: 'easy',
                  resultCode: resultData.resultCode,
                  resultMsg: resultData.resultMsg || '인증 성공',
                  ci: resultData.ci,
                  di: resultData.di,
                  name: resultData.name,
                  birthDate: resultData.birthDate,
                  gender: resultData.gender,
                  nationalInfo: resultData.nationalInfo,
                  mobileNo: resultData.mobileNo,
                  mobileCo: resultData.mobileCo,
                };
                logAuthSuccess('easy', { txId: storedTxId, resultCode: resultData.resultCode });
                optionsRef.current?.onSuccess?.(result);
              } else {
                const error = new Error(resultData.resultMsg || '간편인증 결과 조회에 실패했습니다.');
                logAuthFailure('easy', resultData.resultMsg || '간편인증 결과 조회 실패', {
                  txId: storedTxId,
                  resultCode: resultData.resultCode,
                  resultMsg: resultData.resultMsg,
                  rawResponse: response,
                });
                optionsRef.current?.onError?.(error);
              }
            } else {
              // keyToken 없이 data에서 직접 정보 추출
              const result: UnifiedAuthResult = {
                authMethod: 'easy',
                resultCode: String(data.resultCode),
                resultMsg: data.resultMsg || '인증 성공',
                ci: data.ci,
                di: data.di,
                name: data.name,
                birthDate: data.birthDate,
                gender: data.gender,
                nationalInfo: data.nationalInfo,
                mobileNo: data.mobileNo,
              };
              sessionStorage.removeItem('eziok_txId');
              logAuthSuccess('easy', { resultCode: data.resultCode });
              optionsRef.current?.onSuccess?.(result);
            }
          } else {
            const error = new Error(data.resultMsg || data.errorMsg || '간편인증에 실패했습니다.');
            logAuthFailure('easy', data.resultMsg || data.errorMsg || '간편인증 실패', {
              resultCode: data.resultCode,
              rawResponse: response,
            });
            optionsRef.current?.onError?.(error);
          }
          return;
        }

        // 파이프(|) 구분 문자열 파싱
        const parts = response.split('|');
        const resultCode = parts[0];
        const keyToken = parts[1] || '';
        const errorMsg = parts[2] || '';

        console.log('[UnifiedAuth] Parsed EZIOK:', { resultCode, keyToken, errorMsg });

        // diethelper-front 호환: 성공 코드 배열 체크
        const successCodes = ['0', '0000', '2000'];
        if (successCodes.includes(resultCode)) {
          // 성공 - keyToken으로 백엔드 /result API 호출하여 실제 사용자 정보 조회
          const storedTxId = sessionStorage.getItem('eziok_txId');
          if (!storedTxId) {
            throw new Error('유효하지 않은 인증 요청입니다. (txId 없음)');
          }

          console.log('[UnifiedAuth] Calling /result API with keyToken and txId:', storedTxId);

          // 백엔드 /result API 호출 (diethelper-front와 동일하게 상대 경로 사용)
          const resultResponse = await axiosClient.post('/func/eziok/result', {
            keyToken,
            txId: storedTxId,
            userId: optionsRef.current?.userId,
          });

          console.log('[UnifiedAuth] EZIOK result response:', resultResponse.data);

          // 세션 정리
          sessionStorage.removeItem('eziok_txId');

          const resultData = resultResponse.data;

          if (resultData.resultCode === '0000') {
            const result: UnifiedAuthResult = {
              authMethod: 'easy',
              resultCode: resultData.resultCode,
              resultMsg: resultData.resultMsg || '인증 성공',
              ci: resultData.ci,
              di: resultData.di,
              name: resultData.name,
              birthDate: resultData.birthDate,
              gender: resultData.gender,
              nationalInfo: resultData.nationalInfo,
              mobileNo: resultData.mobileNo,
              mobileCo: resultData.mobileCo,
            };
            // Sentry: 인증 성공 로깅
            logAuthSuccess('easy', { txId: storedTxId, resultCode: resultData.resultCode });
            optionsRef.current?.onSuccess?.(result);
          } else {
            const error = new Error(resultData.resultMsg || '간편인증 결과 조회에 실패했습니다.');
            // Sentry: 인증 실패 로깅
            logAuthFailure('easy', resultData.resultMsg || '간편인증 결과 조회 실패', {
              txId: storedTxId,
              resultCode: resultData.resultCode,
              resultMsg: resultData.resultMsg,
              rawResponse: response,
            });
            optionsRef.current?.onError?.(error);
          }
        } else {
          const error = new Error(errorMsg || '간편인증에 실패했습니다.');
          // Sentry: 인증 실패 로깅 (SDK 응답에서 실패)
          logAuthFailure('easy', errorMsg || '간편인증 실패', {
            resultCode,
            rawResponse: response,
          });
          optionsRef.current?.onError?.(error);
        }
      } catch (err) {
        console.error('[UnifiedAuth] EZIOK callback error:', err);
        // Sentry: 콜백 파싱/처리 에러 로깅
        logCallbackParseError('easy', response, err instanceof Error ? err : new Error('콜백 처리 실패'));
        optionsRef.current?.onError?.(err instanceof Error ? err : new Error('콜백 처리 실패'));
      }
    };

    console.log('[UnifiedAuth] After - eziokCallback:', typeof window.eziokCallback);
    console.log('[UnifiedAuth] Callbacks registered successfully');

    return () => {
      delete window.mobileOKCallback;
      delete window.eziokCallback;
    };
  }, []);

  // 인증 방식 목록 조회
  const getAuthMethods = useCallback(async (): Promise<AuthMethodInfo | null> => {
    try {
      const methods = await UnifiedAuthService.getAuthMethods();
      if (methods) {
        setAuthMethods([methods]);
      }
      return methods;
    } catch (err) {
      console.error('[useUnifiedAuth] Failed to get auth methods:', err);
      return null;
    }
  }, []);

  // 간편인증 사업자 목록 조회
  const getEZIOKProviders = useCallback(async (): Promise<EZIOKProvider[]> => {
    try {
      const providers = await UnifiedAuthService.getEZIOKProviders();
      setEziokProviders(providers);
      return providers;
    } catch (err) {
      console.error('[useUnifiedAuth] Failed to get EZIOK providers:', err);
      return [];
    }
  }, []);

  // 통합 인증 시작
  const startAuth = useCallback(
    async (params: UnifiedAuthRequestParams) => {
      console.log('[UnifiedAuth] startAuth called with:', params);
      // Sentry: 인증 시작 breadcrumb
      addAuthBreadcrumb(
        `Starting ${params.authMethod} authentication`,
        'start',
        { authMethod: params.authMethod, authProvider: params.authProviderCode },
        'info'
      );

      setIsLoading(true);
      setError(null);
      setCurrentAuthMethod(params.authMethod);

      try {
        await UnifiedAuthService.startAuth(params);
        setIsLoading(false);
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error('인증 요청 실패');
        // Sentry: 인증 요청 에러 로깅
        logAuthError(error, {
          authMethod: params.authMethod,
          authProvider: params.authProviderCode,
        }, { 'auth.stage': 'request' });
        setError(error);
        setIsLoading(false);
        options?.onError?.(error);
        throw error;
      }
    },
    [options],
  );

  return {
    startAuth,
    getAuthMethods,
    getEZIOKProviders,
    isLoading,
    error,
    sdkStatus,
    authMethods,
    eziokProviders,
    currentAuthMethod,
    setCurrentAuthMethod,
  };
}

export default useUnifiedAuth;